import { Body, Caption, VBadge, cn } from '@vrzl/ui';
import { Check, ChevronDown, ChevronLeft, Shield } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router';

import { getSelectedTenantLabel } from '@/domains/auth/selectedTenant';
import { useDocuments } from '@/domains/documents';
import { useEvent, useEvents } from '@/domains/events';
import { usePerspectiveIcon } from '@/domains/perspective/usePerspectiveIcon';
import { useMe } from '@/domains/sync/api/me';

import { navItems } from '../lib/navItems';

export function Sidebar() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const BrandIcon = usePerspectiveIcon();
  const { data: event } = useEvent(eventId ?? '');
  const { data: documents } = useDocuments(eventId ?? '');
  const { data: allEvents } = useEvents();
  const { data: me } = useMe();
  const tenantLabel = getSelectedTenantLabel();

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const badgeCounts: Record<string, number | undefined> = {
    documents: documents?.total,
  };

  // Use events list as fallback to avoid flicker when switching events
  const selectedFromList = allEvents?.items.find((e) => e.id === eventId);
  const eventName = event?.name ?? selectedFromList?.name ?? '...';
  const eventExternalId = event?.externalId ?? selectedFromList?.externalId ?? eventId;

  function selectEvent(id: string): void {
    setOpen(false);
    navigate(`/events/${id}/overview`);
  }

  return (
    <aside className="fixed inset-y-0 left-0 z-30 flex w-sidebar flex-col border-r border-sidebar-border bg-sidebar print:hidden">
      {/* Brand */}
      <Link
        to="/"
        className="flex items-center gap-3 px-5 py-5"
      >
        <div className="flex size-8 items-center justify-center rounded-md bg-perspective-600 text-on-primary">
          <BrandIcon className="size-4" />
        </div>
        <div>
          <Body as="span" weight="semibold" className="block text-sidebar-foreground">
            SafeZone
          </Body>
          <Caption>{tenantLabel}</Caption>
        </div>
      </Link>

      {/* Evenementen */}
      <div className="mx-4 border-t border-sidebar-border px-1 pt-4 pb-2">
        <Caption tone="subtle" className="uppercase tracking-wider">
          Evenementen
        </Caption>
      </div>
      <div className="relative px-4 pb-4">
        {eventId && (
          <NavLink
            to={`/events`}
            className={() =>
              cn(
                'flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium mb-2 transition-colors text-sidebar-foreground hover:bg-neutral-200/50'
              )
            }
          >
            <ChevronLeft className="size-4 shrink-0" />
            <span className="flex-1">Alle evenementen</span>
          </NavLink>
        )}
      <div ref={dropdownRef}>
        {/* Custom dropdown trigger — multiline content (event name + externalId + chevron); VButton variants don't fit. */}
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted"
        >
          <div className="min-w-0">
            {eventId ? (
              <>
                <Body weight="semibold" className="truncate">
                  {eventName}
                </Body>
                <Caption>{eventExternalId}</Caption>
              </>
            ) : (
              <Body weight="medium" className="truncate text-muted-foreground">
                Selecteer evenement
              </Body>
            )}
          </div>
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>

        {open && allEvents && (
          <div className="absolute left-4 z-40 mt-1 max-h-64 w-80 overflow-y-auto rounded-lg border border-border bg-popover py-1 shadow-lg">
            {allEvents.items.map((item) => (
              // Dropdown menu item — composite content (name + externalId + check icon) and conditional active background; not a VButton case.
              <button
                key={item.id}
                type="button"
                onClick={() => selectEvent(item.id)}
                className={cn(
                  'flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                  item.id === eventId && 'bg-neutral-200/50',
                )}
              >
                <div className="min-w-0">
                  <Body weight="medium" className="truncate">
                    {item.name}
                  </Body>
                  <Caption>{item.externalId}</Caption>
                </div>
                {item.id === eventId && (
                  <Check className="size-4 shrink-0 text-foreground" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Navigatie */}
      {eventId && (
        <>
          <div className="mx-4 border-t border-sidebar-border px-1 pt-4 pb-2">
            <Caption tone="subtle" className="uppercase tracking-wider">
              Navigatie
            </Caption>
          </div>
          <nav className="flex-1 px-4 py-1">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={`/events/${eventId}/${item.to}`}
                  className={({ isActive }) =>
                    cn(
                      'flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-neutral-200/50 font-semibold text-sidebar-foreground'
                        : 'text-sidebar-foreground hover:bg-neutral-200/50',
                    )
                  }
                >
                  <item.icon className="size-4 shrink-0" />
                  <span className="flex flex-1 items-center gap-2">
                    <span className="truncate">{item.label}</span>
                    {item.tag != null && (
                      <VBadge color="neutral" shape="pill">
                        {item.tag}
                      </VBadge>
                    )}
                  </span>
                  {item.badgeKey != null && badgeCounts[item.badgeKey] != null && (
                    <VBadge color="neutral" shape="pill">
                      {badgeCounts[item.badgeKey]}
                    </VBadge>
                  )}
                </NavLink>
              ))}
            </div>
          </nav>
        </>
      )}

      {/* Bottom — admin (admin-only, altijd zichtbaar) + footer */}
      <div className="mt-auto">
        {me?.isAdmin && (
          <nav className="px-4 pb-1">
            <NavLink
              to={eventId ? `/admin/${eventId}` : '/admin'}
              className={({ isActive }) =>
                cn(
                  'flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-neutral-200/50 font-semibold text-sidebar-foreground'
                    : 'text-sidebar-foreground hover:bg-neutral-200/50',
                )
              }
            >
              <Shield className="size-4 shrink-0" />
              <span className="flex-1">Administratie</span>
            </NavLink>
          </nav>
        )}
        {/* Footer */}
        <div className="mx-4 border-t border-sidebar-border px-1 py-4">
          <Caption tone="subtle">{tenantLabel}</Caption>
        </div>
      </div>
    </aside>
  );
}
